import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import {
  Table,
  Text,
  Autocomplete,
  ActionIcon,
  Badge,
  Box,
  Group,
  Stack,
  Center,
  Loader,
  Select,
  TextInput,
  Button,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconSearch,
  IconScissors,
  IconTrash,
  IconPlus,
  IconRestore,
} from "@tabler/icons-react";
import RichTextDiagnosis from "./RichTextDiagnosis";
import type { VariableConfig } from "./RichTextDiagnosis";
import {
  replaceSystemVariables,
  extractVariables,
  type TemplateContext,
} from "../../utils/grossTemplate";
import { resolveVariableChipValues } from "../shared/tiptap/VariableChip";
import {
  useSourcesWithSubsites,
  useCollectionMethods,
} from "../../hooks/useReferenceData";
import {
  getGrossCodeFragmentCountPatch,
  getFragmentCountDisplayValue,
  normalizeFragmentChipValue,
  type FragmentCountQualifier,
} from "../../utils/fragmentCount";

const MATERIAL_OPTIONS = [
  { value: "formalin_jar", label: "Formalin jar" },
  { value: "block", label: "Block" },
  { value: "slides_only", label: "Slides only" },
  { value: "blood_tube", label: "Blood tube" },
  { value: "urine_sample", label: "Urine sample" },
  { value: "stool_collection", label: "Stool collection" },
];

export interface SpecimenGrossingData {
  specimenId: string;
  grossCode: string;
  grossCodeId: string;
  grossDescription: string;
  fragmentCount?: number | null;
  fragmentCountQualifier?: FragmentCountQualifier | null;
  sourceId?: string | null;
  subsiteId?: string | null;
  subsiteName?: string | null;
  collectionMethod?: string | null;
  findings?: string | null;
  material?: string | null;
  variableValues?: Record<string, string>;
}

interface GrossingCenterProps {
  caseData: any;
  isReadOnly?: boolean;
  grossingDataMap: Map<string, SpecimenGrossingData>;
  onGrossingChange: (
    specimenId: string,
    data: Partial<SpecimenGrossingData>
  ) => void;
  grossCodes: Array<{
    value: string;
    label: string;
    description?: string;
    scenarioDescription?: string;
    fragmentCount?: number | null;
    fragmentCountQualifier?: FragmentCountQualifier | null;
  }>;
  // Structural edits (add, remove, recover) are permission-gated, independent
  // of the diagnosis read-only state.
  canModifyStructure?: boolean;
  canAddSpecimen?: boolean;
  onArchiveSpecimen?: (specimenId: string, specimenNumber: string) => void;
  onAddSpecimen?: () => void;
  onRecoverSpecimens?: () => void;
  archivedCount?: number;
}

function stripHtml(html: string): string {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
}

function stripVariablesAndHtml(text: string): string {
  if (!text) return "";
  const withoutVars = text.replace(/\{\{[^}]+\}\}/g, "");
  return stripHtml(withoutVars).trim();
}

interface RowState {
  macroInput: string;
  macroError: boolean;
}

interface SourceChangePatchInput {
  value: string | null;
  currentSubsiteId?: string | null;
  currentCollectionMethod?: string | null;
  sourceOptions: Array<{
    value: string;
    label: string;
    defaultCollectionMethodName?: string;
  }>;
  subsitesBySource: Record<string, Array<{ value: string; label: string }>>;
  collectionMethodOptions: Array<{ value: string }>;
}

export function buildSourceChangePatch({
  value,
  currentSubsiteId,
  currentCollectionMethod,
  sourceOptions,
  subsitesBySource,
  collectionMethodOptions,
}: SourceChangePatchInput): {
  patch: Partial<SpecimenGrossingData>;
  clearedSubsite: boolean;
} {
  const patch: Partial<SpecimenGrossingData> = { sourceId: value };
  const newSubsites = value ? subsitesBySource[value] || [] : [];
  const clearedSubsite = Boolean(
    currentSubsiteId &&
      !newSubsites.some((subsite) => subsite.value === currentSubsiteId)
  );
  if (clearedSubsite) {
    patch.subsiteId = null;
    patch.subsiteName = null;
  }
  if (value) {
    const methodValid =
      currentCollectionMethod &&
      collectionMethodOptions.some(
        (method) => method.value === currentCollectionMethod
      );
    const selected = sourceOptions.find((source) => source.value === value);
    if (!methodValid && selected?.defaultCollectionMethodName) {
      patch.collectionMethod = selected.defaultCollectionMethodName;
    }
  }
  return { patch, clearedSubsite };
}

function GrossingRow({
  specimen,
  grossingData,
  inputRef,
  onGrossingChange,
  grossCodes,
  templateContext,
  isReadOnly,
  columnCount,
  onFocusNextMacro,
  sourceOptions,
  subsitesBySource,
  collectionMethodOptions,
  canModifyStructure,
  canRemove,
  onArchiveSpecimen,
}: {
  specimen: any;
  grossingData: SpecimenGrossingData;
  inputRef: React.RefObject<HTMLInputElement>;
  onGrossingChange: (data: Partial<SpecimenGrossingData>) => void;
  grossCodes: Array<{
    value: string;
    label: string;
    description?: string;
    scenarioDescription?: string;
    fragmentCount?: number | null;
    fragmentCountQualifier?: FragmentCountQualifier | null;
  }>;
  templateContext: TemplateContext;
  isReadOnly?: boolean;
  columnCount: number;
  onFocusNextMacro?: () => void;
  sourceOptions: Array<{
    value: string;
    label: string;
    defaultCollectionMethodName?: string;
  }>;
  subsitesBySource: Record<string, Array<{ value: string; label: string }>>;
  collectionMethodOptions: Array<{ value: string; label: string }>;
  canModifyStructure?: boolean;
  canRemove?: boolean;
  onArchiveSpecimen?: (specimenId: string, specimenNumber: string) => void;
}) {
  const handleRemoveSpecimen = useCallback(() => {
    onArchiveSpecimen?.(specimen.id, specimen.number);
  }, [onArchiveSpecimen, specimen.id, specimen.number]);

  const currentSourceId =
    grossingData.sourceId !== undefined
      ? grossingData.sourceId
      : specimen.sourceId ?? null;
  const currentSubsiteId =
    grossingData.subsiteId !== undefined
      ? grossingData.subsiteId
      : specimen.subsiteId ?? null;
  const currentCollectionMethod =
    grossingData.collectionMethod !== undefined
      ? grossingData.collectionMethod
      : specimen.collectionMethod ?? null;
  const currentFindings =
    grossingData.findings !== undefined
      ? grossingData.findings
      : specimen.findings ?? null;
  const currentMaterial =
    grossingData.material !== undefined
      ? grossingData.material
      : specimen.material ?? null;
  const currentFragmentCount =
    grossingData.fragmentCount !== undefined
      ? grossingData.fragmentCount
      : specimen.fragmentCount ?? null;
  const currentFragmentCountQualifier =
    grossingData.fragmentCountQualifier !== undefined
      ? grossingData.fragmentCountQualifier
      : specimen.fragmentCountQualifier ?? null;
  const currentFragmentCountDisplay = useMemo(
    () =>
      getFragmentCountDisplayValue(
        currentFragmentCount,
        currentFragmentCountQualifier
      ),
    [currentFragmentCount, currentFragmentCountQualifier]
  );

  const handleFragmentCountChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const patch = normalizeFragmentChipValue(
        event.currentTarget.value,
        currentFragmentCount,
        currentFragmentCountQualifier
      );
      if (patch) onGrossingChange(patch);
    },
    [currentFragmentCount, currentFragmentCountQualifier, onGrossingChange]
  );

  const handleFindingsChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onGrossingChange({ findings: event.currentTarget.value });
    },
    [onGrossingChange]
  );

  const availableSubsites = useMemo(() => {
    const base = currentSourceId ? subsitesBySource[currentSourceId] || [] : [];
    if (!currentSubsiteId) return base;
    const inList = base.some((s) => s.value === currentSubsiteId);
    if (inList) return base;
    const name = specimen?.subsite?.name || "Unknown Subsite";
    return [
      { value: currentSubsiteId, label: `${name} (from previous source)` },
      ...base,
    ];
  }, [
    currentSourceId,
    currentSubsiteId,
    subsitesBySource,
    specimen?.subsite?.name,
  ]);

  const handleSourceChange = useCallback(
    (value: string | null) => {
      const { patch, clearedSubsite } = buildSourceChangePatch({
        value,
        currentSubsiteId,
        currentCollectionMethod,
        sourceOptions,
        subsitesBySource,
        collectionMethodOptions,
      });
      if (clearedSubsite) {
        const newSource = sourceOptions.find((s) => s.value === value);
        notifications.show({
          message: value
            ? `Subsite cleared (not available for ${
                newSource?.label ?? "new source"
              })`
            : "Subsite cleared because no source is selected",
          color: "blue",
          autoClose: 3000,
        });
      }
      onGrossingChange(patch);
    },
    [
      collectionMethodOptions,
      currentCollectionMethod,
      currentSubsiteId,
      onGrossingChange,
      sourceOptions,
      subsitesBySource,
    ]
  );
  const handleSubsiteChange = useCallback(
    (value: string | null) => {
      const selectedSubsite = availableSubsites.find(
        (subsite) => subsite.value === value
      );
      onGrossingChange({
        subsiteId: value,
        subsiteName: selectedSubsite?.label ?? null,
      });
    },
    [availableSubsites, onGrossingChange]
  );
  const [rowState, setRowState] = useState<RowState>({
    macroInput: grossingData.grossCode || "",
    macroError: false,
  });
  const [expandedField, setExpandedField] = useState<"description" | null>(
    null
  );
  const expansionRowRef = useRef<HTMLTableRowElement>(null);
  const dataRowRef = useRef<HTMLTableRowElement>(null);

  const [variableValues, setVariableValues] = useState<Record<string, string>>(
    {}
  );
  const [remainingVars, setRemainingVars] = useState<string[]>([]);
  const varsInitRef = useRef(false);

  // On mount or when description changes, detect variables and auto-fill system ones
  useEffect(() => {
    if (!grossingData.grossDescription) return;
    const allVars = extractVariables(grossingData.grossDescription);
    if (allVars.length === 0) return;

    // Auto-replace system variables if not already done
    const processed = replaceSystemVariables(
      grossingData.grossDescription,
      templateContext
    );
    const wasChanged = processed !== grossingData.grossDescription;
    if (wasChanged) {
      onGrossingChange({ grossDescription: processed });
    }

    // Detect remaining variables after system replacement
    const remaining = extractVariables(
      wasChanged ? processed : grossingData.grossDescription
    );
    if (
      remaining.length > 0 &&
      (remainingVars.length === 0 || !varsInitRef.current)
    ) {
      varsInitRef.current = true;
      setRemainingVars(remaining);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grossingData.grossDescription]);

  useEffect(() => {
    if (!expandedField) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.closest?.(".mantine-Menu-dropdown") ||
        target.closest?.(".mantine-Popover-dropdown")
      ) {
        return;
      }
      if (
        expansionRowRef.current &&
        !expansionRowRef.current.contains(target) &&
        dataRowRef.current &&
        !dataRowRef.current.contains(target)
      ) {
        setExpandedField(null);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [expandedField]);

  useEffect(() => {
    setRowState((s) => {
      if (s.macroInput !== (grossingData.grossCode || "")) {
        return {
          ...s,
          macroInput: grossingData.grossCode || "",
          macroError: false,
        };
      }
      return s;
    });
  }, [grossingData.grossCode]);

  const handleVariableChange = useCallback(
    (varName: string) => (val: string) => {
      setVariableValues((prev) => {
        const next = { ...prev, [varName]: val };
        onGrossingChange({ variableValues: next });
        return next;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const variableConfig: VariableConfig = useMemo(() => {
    const cfg: VariableConfig = {};
    remainingVars.forEach((varName) => {
      cfg[varName] = {
        options: [],
        value: variableValues[varName] || null,
        onChange: handleVariableChange(varName),
      };
    });
    return cfg;
  }, [remainingVars, variableValues, handleVariableChange]);

  const applyMacro = (macro: {
    value: string;
    label: string;
    description?: string;
    fragmentCount?: number | null;
    fragmentCountQualifier?: FragmentCountQualifier | null;
  }): boolean => {
    setRowState((s) => ({ ...s, macroInput: macro.label, macroError: false }));

    const fragmentCountPatch = getGrossCodeFragmentCountPatch(
      macro.fragmentCount,
      macro.fragmentCountQualifier
    ) ?? { fragmentCount: null, fragmentCountQualifier: null };

    const templateText = macro.description || "";
    if (templateText) {
      setVariableValues({});

      const processedTemplate = replaceSystemVariables(
        templateText,
        templateContext
      );
      const vars = extractVariables(processedTemplate);
      setRemainingVars(vars);

      onGrossingChange({
        grossCode: macro.label,
        grossCodeId: macro.value,
        grossDescription: processedTemplate,
        ...fragmentCountPatch,
      });

      if (vars.length > 0) {
        setExpandedField("description");
        return true;
      }
    } else {
      setRemainingVars([]);
      onGrossingChange({
        grossCode: macro.label,
        grossCodeId: macro.value,
        grossDescription: "",
        ...fragmentCountPatch,
      });
    }
    return false;
  };

  const expandMacro = (
    code: string
  ): { expanded: boolean; hasVars: boolean } => {
    if (!code.trim()) {
      setRowState((s) => ({ ...s, macroError: false }));
      return { expanded: false, hasVars: false };
    }
    const macro = grossCodes.find(
      (m) => m.label.toLowerCase() === code.toLowerCase()
    );
    if (!macro) {
      setRowState((s) => ({ ...s, macroError: true }));
      return { expanded: false, hasVars: false };
    }
    const hasVars = applyMacro(macro);
    return { expanded: true, hasVars };
  };

  const handleMacroKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Tab" || e.key === "Enter") {
      const { expanded, hasVars } = expandMacro(rowState.macroInput);
      if (expanded) {
        e.preventDefault();
        if (hasVars) {
          setTimeout(() => {
            const row = expansionRowRef.current;
            if (!row) return;
            setTimeout(() => {
              row
                .querySelector<HTMLInputElement>('input[type="text"]')
                ?.focus();
            }, 50);
          }, 50);
        } else {
          setTimeout(() => onFocusNextMacro?.(), 0);
        }
      } else if (e.key === "Enter") {
        e.preventDefault();
      }
    }
  };

  const handleMacroBlur = () => {
    if (rowState.macroInput && rowState.macroInput !== grossingData.grossCode) {
      expandMacro(rowState.macroInput);
    }
  };

  const handleOptionSubmit = (value: string) => {
    const macro = grossCodes.find(
      (m) => m.label.toLowerCase() === value.toLowerCase()
    );
    if (macro) {
      applyMacro(macro);
      setTimeout(() => onFocusNextMacro?.(), 0);
    }
  };

  const macroSuggestions = useMemo(() => {
    const query = rowState.macroInput.trim().toLowerCase();
    if (!query) return [];

    const searchText = query.length >= 4;

    return grossCodes
      .reduce<
        Array<{
          macro: (typeof grossCodes)[0];
          rank: number;
        }>
      >((acc, m) => {
        const labelLC = m.label.toLowerCase();
        if (labelLC === query) {
          acc.push({ macro: m, rank: 0 });
        } else if (labelLC.startsWith(query)) {
          acc.push({ macro: m, rank: 1 });
        } else if (labelLC.includes(query)) {
          acc.push({ macro: m, rank: 2 });
        } else if (searchText) {
          const plainDesc = stripVariablesAndHtml(m.description || "");
          const plainScenario = stripVariablesAndHtml(
            m.scenarioDescription || ""
          );
          if (plainDesc.toLowerCase().includes(query)) {
            acc.push({ macro: m, rank: 3 });
          } else if (plainScenario.toLowerCase().includes(query)) {
            acc.push({ macro: m, rank: 4 });
          }
        }
        return acc;
      }, [])
      .sort((a, b) => a.rank - b.rank)
      .slice(0, 15)
      .map(({ macro: m }) => ({ value: m.label, label: m.label }));
  }, [rowState.macroInput, grossCodes]);

  const descriptionPlain = useMemo(() => {
    const resolved = resolveVariableChipValues(
      grossingData.grossDescription,
      variableValues
    );
    let text = stripHtml(resolved);

    // Replace remaining {{var}} tokens from live state (handles bare tokens)
    Object.entries(variableValues).forEach(([varName, val]) => {
      if (val) {
        text = text.replace(new RegExp(`\\{\\{${varName}\\}\\}`, "gi"), val);
      }
    });

    // Strip any remaining {{...}} placeholders to ensure raw tokens never appear
    return text.replace(/\{\{[^}]+\}\}/g, "");
  }, [grossingData.grossDescription, variableValues]);

  const handleDescriptionCellClick = () => {
    if (isReadOnly) return;
    setExpandedField((f) => (f === "description" ? null : "description"));
  };

  return (
    <>
      <Table.Tr ref={dataRowRef}>
        {/* ID */}
        <Table.Td style={{ width: 56, verticalAlign: "middle" }}>
          <Badge
            color="gray"
            variant="filled"
            size="md"
            style={{ fontFamily: "monospace" }}
          >
            {specimen.number}
          </Badge>
        </Table.Td>

        {/* Source */}
        <Table.Td style={{ verticalAlign: "middle" }}>
          <Select
            size="xs"
            placeholder="Select"
            data={sourceOptions}
            value={currentSourceId}
            onChange={handleSourceChange}
            searchable
            clearable
            disabled={isReadOnly}
          />
        </Table.Td>

        {/* Subsite */}
        <Table.Td style={{ verticalAlign: "middle" }}>
          <Select
            size="xs"
            placeholder="Select"
            data={availableSubsites}
            value={currentSubsiteId}
            onChange={handleSubsiteChange}
            searchable
            clearable
            disabled={
              isReadOnly ||
              (availableSubsites.length === 0 && !currentSubsiteId)
            }
          />
        </Table.Td>

        {/* Findings */}
        <Table.Td style={{ verticalAlign: "middle" }}>
          <TextInput
            size="xs"
            placeholder="Enter findings"
            aria-label={`Findings for specimen ${specimen.number}`}
            value={currentFindings ?? ""}
            onChange={handleFindingsChange}
            disabled={isReadOnly}
          />
        </Table.Td>

        {/* Collection Method */}
        <Table.Td style={{ verticalAlign: "middle" }}>
          <Select
            size="xs"
            placeholder="Select"
            data={collectionMethodOptions}
            value={currentCollectionMethod}
            onChange={(value) => onGrossingChange({ collectionMethod: value })}
            searchable
            clearable
            disabled={isReadOnly}
          />
        </Table.Td>

        {/* Material */}
        <Table.Td style={{ verticalAlign: "middle" }}>
          <Select
            size="xs"
            placeholder="Select"
            data={MATERIAL_OPTIONS}
            value={currentMaterial}
            onChange={(value) => onGrossingChange({ material: value })}
            searchable
            clearable
            disabled={isReadOnly}
          />
        </Table.Td>

        {/* Fragment Count */}
        <Table.Td style={{ verticalAlign: "middle", textAlign: "center" }}>
          <TextInput
            size="xs"
            placeholder="# or Multiple"
            value={currentFragmentCountDisplay}
            onChange={handleFragmentCountChange}
            inputMode="numeric"
            disabled={isReadOnly}
            styles={{ input: { textAlign: "center" } }}
          />
        </Table.Td>

        {/* Gross Template (Code) */}
        <Table.Td style={{ width: 130, verticalAlign: "middle" }}>
          <Autocomplete
            ref={inputRef}
            value={rowState.macroInput}
            onChange={(val) =>
              setRowState((s) => ({
                ...s,
                macroInput: val.toUpperCase(),
                macroError: false,
              }))
            }
            onKeyDown={handleMacroKeyDown}
            onBlur={handleMacroBlur}
            onOptionSubmit={handleOptionSubmit}
            data={macroSuggestions}
            filter={({ options }) => options}
            renderOption={({ option }) => {
              const macro = grossCodes.find((m) => m.label === option.value);
              const preview = macro
                ? stripVariablesAndHtml(macro.description || "")
                : "";
              return (
                <Group gap="xs" wrap="nowrap">
                  <Text
                    size="xs"
                    fw={700}
                    ff="monospace"
                    style={{ flexShrink: 0 }}
                  >
                    {option.value}
                  </Text>
                  {preview && (
                    <Text
                      size="xs"
                      c="dimmed"
                      lineClamp={1}
                      style={{ flex: 1, overflow: "hidden" }}
                    >
                      {preview}
                    </Text>
                  )}
                </Group>
              );
            }}
            placeholder="Code"
            disabled={isReadOnly}
            error={rowState.macroError ? "Not found" : undefined}
            size="xs"
            styles={{
              input: {
                fontFamily: "monospace",
                fontWeight: 600,
                textTransform: "uppercase",
              },
            }}
            rightSection={
              <ActionIcon
                size="xs"
                variant="subtle"
                color="gray"
                title="Search gross codes"
                disabled={isReadOnly}
              >
                <IconSearch size={11} />
              </ActionIcon>
            }
            rightSectionPointerEvents="all"
          />
        </Table.Td>

        {/* Description */}
        <Table.Td
          style={{
            verticalAlign: "middle",
            cursor: isReadOnly ? "default" : "pointer",
            backgroundColor:
              expandedField === "description"
                ? "var(--mantine-color-blue-0)"
                : undefined,
          }}
          onClick={handleDescriptionCellClick}
        >
          {descriptionPlain ? (
            <Text
              size="sm"
              lineClamp={2}
              title={descriptionPlain}
              style={{ wordBreak: "break-word" }}
            >
              {descriptionPlain}
            </Text>
          ) : (
            <Text size="sm" c="dimmed" fs="italic">
              —
            </Text>
          )}
        </Table.Td>

        {/* Actions */}
        <Table.Td style={{ verticalAlign: "middle", textAlign: "center" }}>
          {canModifyStructure && onArchiveSpecimen && (
            <Tooltip
              label={
                canRemove ? "Remove specimen" : "A case must keep one specimen"
              }
              withArrow
            >
              <ActionIcon
                variant="subtle"
                color="red"
                size="sm"
                aria-label={`Remove specimen ${specimen.number}`}
                disabled={!canRemove}
                onClick={handleRemoveSpecimen}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Tooltip>
          )}
        </Table.Td>
      </Table.Tr>

      {/* Inline TipTap editor expansion row */}
      {expandedField && (
        <Table.Tr ref={expansionRowRef}>
          <Table.Td colSpan={columnCount} style={{ padding: "12px 16px" }}>
            <RichTextDiagnosis
              label="Gross Description"
              value={grossingData.grossDescription || ""}
              onChange={(val) => onGrossingChange({ grossDescription: val })}
              disabled={isReadOnly}
              minHeight={120}
              autoFocus
              variableConfig={variableConfig}
            />
          </Table.Td>
        </Table.Tr>
      )}
    </>
  );
}

export function GrossingCenter({
  caseData,
  isReadOnly = false,
  grossingDataMap,
  onGrossingChange,
  grossCodes,
  canModifyStructure,
  canAddSpecimen,
  onArchiveSpecimen,
  onAddSpecimen,
  onRecoverSpecimens,
  archivedCount,
}: GrossingCenterProps) {
  const columnCount = 10;

  const { data: sourcesData } = useSourcesWithSubsites();
  const { data: collectionMethods = [] } = useCollectionMethods();

  const sourceOptions = useMemo(
    () => sourcesData?.sources || [],
    [sourcesData?.sources]
  );
  const subsitesBySource = useMemo(
    () => sourcesData?.subsitesBySource || {},
    [sourcesData?.subsitesBySource]
  );
  const collectionMethodOptions = useMemo(
    () => collectionMethods,
    [collectionMethods]
  );

  const specimens = useMemo(() => {
    if (!caseData?.specimens) return [];
    return [...caseData.specimens].sort((a: any, b: any) =>
      (a.number || "").localeCompare(b.number || "")
    );
  }, [caseData?.specimens]);

  const inputRefsMap = useRef<Map<string, React.RefObject<HTMLInputElement>>>(
    new Map()
  );
  specimens.forEach((s: any) => {
    if (!inputRefsMap.current.has(s.id)) {
      inputRefsMap.current.set(s.id, { current: null });
    }
  });

  if (!caseData) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  if (specimens.length === 0) {
    return (
      <Stack gap="md">
        <Group gap="xs">
          <IconScissors size={18} />
          <Text fw={600} size="sm">
            GROSSING
          </Text>
        </Group>
        <Center py="xl">
          <Stack align="center" gap="xs">
            <IconScissors size={32} color="var(--mantine-color-dimmed)" />
            <Text size="sm" c="dimmed">
              No specimens found for this case
            </Text>
          </Stack>
        </Center>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="xs">
          <IconScissors size={18} />
          <Text fw={600} size="sm">
            GROSSING
          </Text>
          <Badge size="sm" variant="light" color="gray">
            {specimens.length} specimen{specimens.length !== 1 ? "s" : ""}
          </Badge>
        </Group>
        {canModifyStructure && (
          <Group gap="xs">
            {canAddSpecimen !== false && (
              <Button
                size="xs"
                variant="light"
                leftSection={<IconPlus size={14} />}
                onClick={onAddSpecimen}
              >
                Add specimen
              </Button>
            )}
            {(archivedCount ?? 0) > 0 && (
              <Button
                size="xs"
                variant="light"
                color="orange"
                leftSection={<IconRestore size={14} />}
                onClick={onRecoverSpecimens}
              >
                Recover ({archivedCount})
              </Button>
            )}
          </Group>
        )}
      </Group>

      <Box>
        <Table.ScrollContainer minWidth={1100}>
          <Table
            withTableBorder
            withColumnBorders
            highlightOnHover
            verticalSpacing="xs"
            horizontalSpacing="xs"
            styles={{
              th: {
                whiteSpace: "nowrap",
                fontSize: "var(--mantine-font-size-xs)",
                fontWeight: 600,
                textTransform: "uppercase",
                color: "var(--mantine-color-dimmed)",
              },
            }}
          >
            <colgroup>
              <col style={{ width: 56 }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "13%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: 70 }} />
              <col style={{ width: 130 }} />
              <col style={{ width: "auto" }} />
              <col style={{ width: 48 }} />
            </colgroup>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>ID</Table.Th>
                <Table.Th>Source</Table.Th>
                <Table.Th>Subsite</Table.Th>
                <Table.Th>Findings</Table.Th>
                <Table.Th>Collection Method</Table.Th>
                <Table.Th>Material</Table.Th>
                <Table.Th>Fragments</Table.Th>
                <Table.Th>Gross Template</Table.Th>
                <Table.Th>Description</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {specimens.map((specimen: any, index: number) => {
                const grossingData = grossingDataMap.get(specimen.id) || {
                  specimenId: specimen.id,
                  grossCode: "",
                  grossCodeId: "",
                  grossDescription: "",
                };

                const inputRef = inputRefsMap.current.get(specimen.id)!;

                const specimenContext: TemplateContext = {
                  specimenLetter: specimen.number,
                  specimenCount: specimens.length,
                  sourceName: specimen.source?.name,
                  subsiteName: specimen.subsite?.name,
                  collectionMethod: specimen.collectionMethod,
                  blockCount: specimen.blocks?.length || 0,
                  cassetteCount: specimen.blocks?.length || 0,
                };

                const handleFocusNextMacro = () => {
                  const remaining = specimens.slice(index + 1);
                  const nextEmpty = remaining.find((s: any) => {
                    const ref = inputRefsMap.current.get(s.id);
                    return ref?.current && !ref.current.value.trim();
                  });
                  if (nextEmpty) {
                    const ref = inputRefsMap.current.get(nextEmpty.id);
                    ref?.current?.focus();
                  } else {
                    inputRef.current?.blur();
                  }
                };

                return (
                  <GrossingRow
                    key={specimen.id}
                    specimen={specimen}
                    grossingData={grossingData}
                    inputRef={inputRef}
                    onGrossingChange={(data) =>
                      onGrossingChange(specimen.id, data)
                    }
                    grossCodes={grossCodes}
                    templateContext={specimenContext}
                    isReadOnly={isReadOnly}
                    columnCount={columnCount}
                    onFocusNextMacro={handleFocusNextMacro}
                    sourceOptions={sourceOptions}
                    subsitesBySource={subsitesBySource}
                    collectionMethodOptions={collectionMethodOptions}
                    canModifyStructure={canModifyStructure}
                    canRemove={
                      (canModifyStructure ?? false) && specimens.length > 1
                    }
                    onArchiveSpecimen={onArchiveSpecimen}
                  />
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Box>
    </Stack>
  );
}
